import React from "react";
import { Box, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { connectionSupplement, ondConnectionComplete, ondConnectionState, positive } from "./ondConnectionData";
export { ondConnectionComplete } from "./ondConnectionData";

export default function OndConnectionSettings({ characteristics: chars = {}, onChange, greek = false, disabled = false }) {
  const tr = (el, en) => greek ? el : en;
  const state = ondConnectionState(chars);
  const { definition: value, known, needsSource, facts } = state;
  const { mode, n_mppt: count, n_inputs: terminals, mppts: groups, inputs } = value;
  const update = (patch) => onChange(connectionSupplement(state, patch));
  const field = (label, val, change, integer = false) => <TextField size="small" type="number" label={label}
    value={val ?? ""} onChange={(event) => change(event.target.value)} disabled={disabled}
    inputProps={{ min: integer ? 1 : 0, step: integer ? 1 : "any" }} sx={{ width: 190 }} />;
  const samePoint = mode === "direct" && count === 1 && terminals === 1;
  const groupField = (index, key, val) => {
    const patch = { mppts: groups.map((row, i) => i === index ? { ...row, [key]: val } : row) };
    if (samePoint) patch.inputs = inputs.map((row) => ({ ...row, [key]: val }));
    update(patch);
  };
  return <Stack spacing={2} sx={{ p: 2, border: 1, borderColor: "divider", borderRadius: 1 }}>
    <Typography fontWeight={600}>{tr("Σύνδεση εισόδων DC", "DC input connection")}</Typography>
    <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
      <Typography variant="body2" fontWeight={600}>{tr("Από το OND", "From the OND")}</Typography>
      <Typography variant="body2">
        {known.n_mppt ? `${count} MPPT` : tr("Πλήθος MPPT: δεν δηλώνεται", "MPPT count: not provided")}
        {" · "}{known.n_inputs ? tr(`${terminals} φυσικές είσοδοι DC`, `${terminals} physical DC inputs`)
          : tr("Πλήθος εισόδων: δεν δηλώνεται", "Input count: not provided")}
      </Typography>
      {facts.operating_a > 0 && <Typography variant="body2">{tr("Συνολικό μέγ. ρεύμα DC", "Total DC operating limit")}: {facts.operating_a} A</Typography>}
      {facts.short_circuit_a > 0 && <Typography variant="body2">{tr("Συνολικό μέγ. Isc", "Total short-circuit limit")}: {facts.short_circuit_a} A</Typography>}
    </Box>
    <Typography variant="body2" color="text.secondary">
      {tr("Συμπληρώστε μόνο όσα λείπουν. Τα στοιχεία του αρχείου χρησιμοποιούνται αυτόματα· το συνολικό ρεύμα δεν μοιράζεται αυθαίρετα στις εισόδους.",
        "Complete only missing data. File values are reused automatically; total current is not divided among inputs without evidence.")}
    </Typography>
    <TextField select size="small" label={tr("Τρόπος σύνδεσης", "Connection type")} value={mode} disabled={disabled}
      onChange={(event) => update({ mode: event.target.value })}>
      <MenuItem value="unknown">{tr("Δεν γνωρίζω ακόμη", "Not known yet")}</MenuItem>
      <MenuItem value="direct">{tr("Απευθείας — ένα string ανά φυσική είσοδο", "Direct — one string per physical input")}</MenuItem>
      <MenuItem value="combiner">{tr("Εξωτερική συνένωση DC", "External DC combining")}</MenuItem>
    </TextField>
    {mode !== "unknown" && <>
      {(!known.n_mppt || !known.n_inputs) && <Stack direction="row" flexWrap="wrap" gap={1}>
        {!known.n_mppt && field(tr("Αριθμός MPPT", "MPPT count"), count || "", (v) => update({ n_mppt: v }), true)}
        {!known.n_inputs && field(tr("Φυσικές είσοδοι DC", "Physical DC input count"), terminals || "", (v) => update({ n_inputs: v }), true)}
      </Stack>}
      {needsSource && <TextField size="small" label={tr("Πηγή συμπληρωματικών στοιχείων", "Source for missing data")}
        helperText={tr("Τεχνικό φυλλάδιο ή αναφορά κατασκευαστή για τα στοιχεία που συμπληρώνετε.", "Datasheet or manufacturer reference for the values you add.")}
        value={value.source} onChange={(event) => update({ source: event.target.value })} disabled={disabled} />}
      {count > 1 && !positive(facts.operating_a) && field(tr("Συνολικό μέγ. ρεύμα DC (A)", "Total DC operating limit (A)"), value.total_operating_a,
        (v) => update({ total_operating_a: v }))}
      {groups.map((group, index) => (!known.mppts[index].operating_a || !known.mppts[index].short_circuit_a) && <Box key={index}>
        <Typography variant="body2" sx={{ mb: 1 }}>MPPT {index + 1}{samePoint ? tr(" / είσοδος DC", " / DC input") : ""}</Typography>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {!known.mppts[index].operating_a && field(tr("Μέγ. ρεύμα MPPT (A)", "MPPT operating limit (A)"), group.operating_a,
            (v) => groupField(index, "operating_a", v))}
          {!known.mppts[index].short_circuit_a && field(tr("Μέγ. Isc MPPT (A)", "MPPT short-circuit limit (A)"), group.short_circuit_a,
            (v) => groupField(index, "short_circuit_a", v))}
        </Stack>
      </Box>)}
      {!samePoint && inputs.map((input, index) => {
        const set = (key, val) => update({ inputs: inputs.map((row, i) => i === index ? { ...row, [key]: val } : row) });
        return <Box key={index}>
          <Typography variant="body2" sx={{ mb: 1 }}>{tr("Φυσική είσοδος DC", "Physical DC input")} {index + 1}</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {!known.inputs[index].mppt && <TextField select size="small" label="MPPT" value={input.mppt} onChange={(event) => set("mppt", event.target.value)} disabled={disabled} sx={{ width: 105 }}>
              {groups.map((_, i) => <MenuItem key={i} value={i + 1}>{i + 1}</MenuItem>)}
            </TextField>}
            {!known.inputs[index].operating_a && field(tr("Μέγ. ρεύμα εισόδου (A)", "Input operating limit (A)"), input.operating_a, (v) => set("operating_a", v))}
            {!known.inputs[index].short_circuit_a && field(tr("Μέγ. Isc εισόδου (A)", "Input short-circuit limit (A)"), input.short_circuit_a, (v) => set("short_circuit_a", v))}
            {mode === "combiner" && <>
              {field(tr("Μέγ. strings συνένωσης", "Combiner string limit"), input.max_strings, (v) => set("max_strings", v), true)}
              {field(tr("Τάση συνένωσης (V)", "Combiner voltage rating (V)"), input.voltage_v, (v) => set("voltage_v", v))}
            </>}
          </Stack>
        </Box>;
      })}
    </>}
    <Box role="status" sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
      <Typography variant="body2">
        {ondConnectionComplete(chars)
          ? mode === "combiner" ? tr("Οι έλεγχοι χρησιμοποιούν τα στοιχεία του OND και τα συμπληρωματικά όρια. Η προτεινόμενη συνένωση δεν αποτελεί πιστοποίηση καλωδίωσης και προστασιών.",
            "Checks use OND values and supplementary ratings. The proposed combining does not certify wiring or protection devices.")
            : tr("Τα απαιτούμενα στοιχεία σύνδεσης είναι διαθέσιμα. Ο έλεγχος strings χρησιμοποιεί τα όρια του OND και όσα συμπληρώσατε.",
              "Required connection data are available. String checks use OND limits and any data you supplied.")
          : tr("Μη επαληθευμένη σύνδεση DC. Το μοντέλο μπορεί να αποθηκευτεί στον κατάλογο· σχεδιασμός και προσομοίωση παραμένουν μπλοκαρισμένα μέχρι να συμπληρωθούν τα στοιχεία που λείπουν.",
            "DC connection not verified. The model can be saved in the catalogue; design and simulation remain blocked until missing data are supplied.")}
      </Typography>
    </Box>
  </Stack>;
}
